import { formatDuration } from 'admin-panel/utils/dashboard-formatters';

import type { DashboardMetricFormat } from 'admin-panel/types/dashboard';

export const DEFAULT_CHART_WIDTH = 554;
export const CHART_HEIGHT = 235;
export const CHART_PADDING_TOP = 0;
export const CHART_PADDING_BOTTOM = 56;
export const CHART_PADDING_RIGHT = 32;
export const X_AXIS_LABEL_GAP = 12;
export const X_AXIS_VALUE_LABEL_HEIGHT = 10;
export const X_AXIS_LEGEND_GAP = 12;
export const TOOLTIP_HEIGHT = 50;
export const TOOLTIP_SHADOW_BLEED = 8;
export const TOOLTIP_HOST_HEIGHT = TOOLTIP_HEIGHT + TOOLTIP_SHADOW_BLEED * 2;

const X_AXIS_LABEL_ESTIMATED_CHARACTER_WIDTH = 6;
const X_AXIS_LABEL_MIN_GAP = 8;
const TOOLTIP_MAX_WIDTH = 240;
const TOOLTIP_HORIZONTAL_PADDING = 24;
const TOOLTIP_TITLE_CHARACTER_WIDTH = 6;
const TOOLTIP_VALUE_CHARACTER_WIDTH = 7;
const DURATION_TICK_MINUTES = [1, 2, 5, 10, 15, 30, 60];
const MAX_DURATION_TICK_INTERVALS = 9;
const SECONDS_IN_MINUTE = 60;
const STACKED_TOOLTIP_PADDING_X = 24;
const STACKED_TOOLTIP_PADDING_Y = 16;
const STACKED_TOOLTIP_TITLE_HEIGHT = 14;
const STACKED_TOOLTIP_ROW_HEIGHT = 22;
const STACKED_TOOLTIP_ICON_WIDTH = 22;
const STACKED_TOOLTIP_VALUE_CHARACTER_WIDTH = 8.5;

export function getElementWidth(element: HTMLElement) {
  return Math.round(
    element.getBoundingClientRect().width ||
      element.clientWidth ||
      DEFAULT_CHART_WIDTH
  );
}

export function clamp(value: number, min: number, max: number) {
  const safeMax = Math.max(min, max);

  return Math.min(Math.max(value, min), safeMax);
}

export function getNiceAxisTickValues(maxValue: number) {
  const tickCount = 5;
  const step = getNiceStep(maxValue / tickCount);
  const maxTick = Math.floor(maxValue / step) * step;
  const ticks: number[] = [];

  for (let value = step; value <= maxTick + Number.EPSILON; value += step) {
    ticks.push(roundTickValue(value));
  }

  return ticks;
}

export function getAverageDurationTickValues(values: number[]) {
  const maxAverageDuration = Math.max(...values, 0);

  if (maxAverageDuration <= 2 * SECONDS_IN_MINUTE) {
    return [0, 30, 60, 90, 120];
  }

  const minutes =
    DURATION_TICK_MINUTES.find(
      (step) =>
        maxAverageDuration / (step * SECONDS_IN_MINUTE) <=
        MAX_DURATION_TICK_INTERVALS
    ) ??
    Math.ceil(
      maxAverageDuration / SECONDS_IN_MINUTE / MAX_DURATION_TICK_INTERVALS
    );
  const step = minutes * SECONDS_IN_MINUTE;

  return Array.from(
    { length: Math.ceil(maxAverageDuration / step) + 1 },
    (_value, index) => index * step
  );
}

export function formatDashboardDurationAxis(seconds: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(seconds / SECONDS_IN_MINUTE);
}

export function estimateLabelWidth(label: string) {
  return (
    label.length * X_AXIS_LABEL_ESTIMATED_CHARACTER_WIDTH + X_AXIS_LABEL_MIN_GAP
  );
}

export function getTooltipWidth(title: string, value: string) {
  return clamp(
    Math.ceil(
      Math.max(
        title.length * TOOLTIP_TITLE_CHARACTER_WIDTH,
        value.length * TOOLTIP_VALUE_CHARACTER_WIDTH
      ) + TOOLTIP_HORIZONTAL_PADDING
    ),
    1,
    TOOLTIP_MAX_WIDTH
  );
}

export function getStackedTooltipWidth(title: string, valueLabels: string[]) {
  const widestRow = Math.max(
    ...valueLabels.map(
      (label) =>
        STACKED_TOOLTIP_ICON_WIDTH +
        label.length * STACKED_TOOLTIP_VALUE_CHARACTER_WIDTH
    ),
    0
  );

  return clamp(
    Math.ceil(
      Math.max(title.length * TOOLTIP_TITLE_CHARACTER_WIDTH, widestRow) +
        STACKED_TOOLTIP_PADDING_X
    ),
    1,
    TOOLTIP_MAX_WIDTH
  );
}

export function getStackedTooltipHeight(rowCount: number) {
  return (
    STACKED_TOOLTIP_PADDING_Y +
    STACKED_TOOLTIP_TITLE_HEIGHT +
    rowCount * STACKED_TOOLTIP_ROW_HEIGHT
  );
}

export function formatDashboardTooltipValue(
  value: number,
  format: DashboardMetricFormat,
  locale: string,
  currencyCode: string,
  fractionDigits?: number
) {
  if (format === 'duration') {
    return formatDuration(value, locale, 'short');
  }

  if (format === 'currency') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  const maximumFractionDigits = fractionDigits ?? 0;
  const roundsToZero =
    value !== 0 && Math.abs(value) < 0.5 / 10 ** maximumFractionDigits;

  return new Intl.NumberFormat(
    locale,
    roundsToZero
      ? { maximumSignificantDigits: 1 }
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits }
  ).format(value);
}

function roundTickValue(value: number) {
  return Number(value.toPrecision(12));
}

function getNiceStep(rawStep: number) {
  if (rawStep <= 0) {
    return 1;
  }

  const exponent = Math.floor(Math.log10(rawStep));
  const magnitude = 10 ** exponent;
  const fraction = rawStep / magnitude;

  if (fraction <= 1.5) {
    return magnitude;
  }

  if (fraction <= 3) {
    return 2 * magnitude;
  }

  if (fraction <= 7) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
}
